import type { DeliveryZone, DeliveryZonePoint } from '@choco-admin/types';

/**
 * Client-side guards for delivery zones.
 *
 * Every rule here mirrors one the backend enforces in
 * `supplierService/src/service/validator/assertValidDeliveryZones.ts`. The
 * backend is the authority; these run first so a mistake is caught while the
 * shape is still on screen and editable, rather than as a rejected save.
 */

/** The backend's own ceiling (`MAX_DELIVERY_ZONE_POINTS`). */
export const MAX_ZONE_POINTS = 1000;

/** Three distinct corners is the minimum that encloses any area at all. */
export const MIN_ZONE_POINTS = 3;

export enum DeliveryZoneProblem {
  NAME_REQUIRED = 'NAME_REQUIRED',
  NAME_TAKEN = 'NAME_TAKEN',
  TOO_FEW_POINTS = 'TOO_FEW_POINTS',
  TOO_MANY_POINTS = 'TOO_MANY_POINTS',
  REPEATED_POINT = 'REPEATED_POINT',
  SELF_INTERSECTS = 'SELF_INTERSECTS',
}

/**
 * What CS reads when a zone is rejected.
 *
 * Each message says what is wrong and what to do about it, in the words of
 * someone drawing an area on a map rather than someone reading a polygon
 * validator.
 */
export const DELIVERY_ZONE_MESSAGES: Record<DeliveryZoneProblem, string> = {
  [DeliveryZoneProblem.NAME_REQUIRED]: 'Give the area a name before saving it.',
  [DeliveryZoneProblem.NAME_TAKEN]:
    'This distributor already has an area with that name. Pick a different one.',
  [DeliveryZoneProblem.TOO_FEW_POINTS]: `An area needs at least ${MIN_ZONE_POINTS} corners. Add more points to close the shape.`,
  // MAX_ZONE_POINTS counts the transmitted ring, whose last point repeats the
  // first, so the number of corners someone can actually place is one fewer.
  // Quoting the wire limit here would send them back to redraw a shape the
  // portal had already accepted.
  [DeliveryZoneProblem.TOO_MANY_POINTS]: `An area can have at most ${
    MAX_ZONE_POINTS - 1
  } corners. Draw it with fewer points.`,
  [DeliveryZoneProblem.REPEATED_POINT]:
    'Two corners sit on top of each other. Drag one of them apart.',
  [DeliveryZoneProblem.SELF_INTERSECTS]:
    'The outline crosses over itself. Reshape it so the edges never cross.',
};

/** Backend error keys, mapped to the same wording as the local checks. */
const BACKEND_ERROR_MESSAGES: Record<string, string> = {
  'deliveryZone.duplicateName':
    DELIVERY_ZONE_MESSAGES[DeliveryZoneProblem.NAME_TAKEN],
  'deliveryZone.maxPoints':
    DELIVERY_ZONE_MESSAGES[DeliveryZoneProblem.TOO_MANY_POINTS],
  'deliveryZone.selfIntersects':
    DELIVERY_ZONE_MESSAGES[DeliveryZoneProblem.SELF_INTERSECTS],
  'deliveryZone.invalidPolygon':
    'Google could not read that shape as an area. Redraw it and try again.',
};

const hasMessage = (value: unknown): value is { message: string } =>
  typeof value === 'object' &&
  value !== null &&
  'message' in value &&
  typeof Reflect.get(value, 'message') === 'string';

/**
 * Turn a save failure into something CS can act on.
 *
 * The backend reports these as bare message strings rather than error codes, and
 * Apollo prefixes them, so the key is matched anywhere in the text.
 *
 * The value handed in is a plain GraphQLError as often as it is an `Error`,
 * because the client resolves failed mutations rather than throwing. Reading the
 * message off either shape avoids stringifying an object into "[object Object]"
 * and losing the key.
 */
export const describeDeliveryZoneSaveError = (error: unknown): string => {
  const raw = hasMessage(error) ? error.message : String(error);
  const match = Object.keys(BACKEND_ERROR_MESSAGES).find((key) =>
    raw.includes(key)
  );
  return match
    ? BACKEND_ERROR_MESSAGES[match]
    : 'The delivery areas could not be saved. Try again, and report it if it keeps failing.';
};

const samePoint = (a: DeliveryZonePoint, b: DeliveryZonePoint): boolean =>
  a.latitude === b.latitude && a.longitude === b.longitude;

/**
 * Whether two segments cross at a point interior to both.
 *
 * Shared endpoints do not count: consecutive edges of any polygon meet at a
 * corner, and a closed ring's first and last edges meet at the start point.
 */
const segmentsCross = (
  p1: DeliveryZonePoint,
  p2: DeliveryZonePoint,
  p3: DeliveryZonePoint,
  p4: DeliveryZonePoint
): boolean => {
  const dx1 = p2.longitude - p1.longitude;
  const dy1 = p2.latitude - p1.latitude;
  const dx2 = p4.longitude - p3.longitude;
  const dy2 = p4.latitude - p3.latitude;

  const denominator = dx1 * dy2 - dy1 * dx2;
  if (denominator === 0) return false; // parallel or collinear

  const t =
    ((p3.longitude - p1.longitude) * dy2 - (p3.latitude - p1.latitude) * dx2) /
    denominator;
  const u =
    ((p3.longitude - p1.longitude) * dy1 - (p3.latitude - p1.latitude) * dx1) /
    denominator;

  return t > 0 && t < 1 && u > 0 && u < 1;
};

/** Whether the ring's edges cross each other anywhere. */
export const isSelfIntersecting = (points: DeliveryZonePoint[]): boolean => {
  const count = points.length;
  if (count < 4) return false;

  for (let i = 0; i < count; i += 1) {
    const a1 = points[i];
    const a2 = points[(i + 1) % count];

    // Start two edges along: the neighbouring edge shares a corner by design.
    for (let j = i + 2; j < count; j += 1) {
      // The closing edge touches the first edge at the start point, which is a
      // corner rather than a crossing.
      const isClosingPair = i === 0 && j === count - 1;
      if (
        !isClosingPair &&
        segmentsCross(a1, a2, points[j], points[(j + 1) % count])
      ) {
        return true;
      }
    }
  }

  return false;
};

const firstEqualsLast = (points: DeliveryZonePoint[]): boolean =>
  points.length > 1 && samePoint(points[0], points[points.length - 1]);

/**
 * Repeat the first corner at the end.
 *
 * The backend validates zones as GeoJSON, where a ring must close on itself.
 * Sending four distinct corners of a rectangle is rejected outright with
 * `deliveryZone.invalidPolygon` (verified live on chocoautumn), so the closing
 * point is added here rather than being asked of the person drawing.
 */
export const closeRing = (points: DeliveryZonePoint[]): DeliveryZonePoint[] =>
  points.length < MIN_ZONE_POINTS || firstEqualsLast(points)
    ? points
    : [...points, { ...points[0] }];

/**
 * Drop a repeated closing corner.
 *
 * Saved zones come back closed. The editor works in corners a person placed, so
 * the duplicate is removed on the way in; otherwise every reopened zone gains a
 * phantom vertex stacked on its first one.
 */
export const openRing = (points: DeliveryZonePoint[]): DeliveryZonePoint[] =>
  firstEqualsLast(points) ? points.slice(0, -1) : points;

interface ValidateZoneParams {
  /** The zone as drawn, with the name CS typed. */
  zone: DeliveryZone;
  /** Every other zone on this distributor, for the name check. */
  otherZones: DeliveryZone[];
}

/**
 * The problems with one zone, worst first, or an empty list when it is fine.
 */
export const findDeliveryZoneProblems = ({
  zone,
  otherZones,
}: ValidateZoneParams): DeliveryZoneProblem[] => {
  const problems: DeliveryZoneProblem[] = [];
  const name = zone.name.trim();

  if (!name) {
    problems.push(DeliveryZoneProblem.NAME_REQUIRED);
  } else if (
    // Stricter than the backend on purpose: it compares names exactly, so
    // "Berlin" and "berlin" both save and then read as duplicates to a human.
    otherZones.some(
      (other) => other.name.trim().toLowerCase() === name.toLowerCase()
    )
  ) {
    problems.push(DeliveryZoneProblem.NAME_TAKEN);
  }

  if (zone.points.length < MIN_ZONE_POINTS) {
    problems.push(DeliveryZoneProblem.TOO_FEW_POINTS);
  }

  // The ceiling applies to what is transmitted, and the transmitted ring is
  // closed (see closeRing), so a shape drawn with exactly MAX_ZONE_POINTS
  // corners would serialize to one point too many and be rejected on save.
  // Validate the closed ring rather than the drawn corners.
  if (closeRing(zone.points).length > MAX_ZONE_POINTS) {
    problems.push(DeliveryZoneProblem.TOO_MANY_POINTS);
  }

  const hasRepeat = zone.points.some(
    (point, index) => index > 0 && samePoint(point, zone.points[index - 1])
  );
  if (hasRepeat) {
    problems.push(DeliveryZoneProblem.REPEATED_POINT);
  }

  if (isSelfIntersecting(zone.points)) {
    problems.push(DeliveryZoneProblem.SELF_INTERSECTS);
  }

  return problems;
};

/** The first problem's message, or undefined when the zone is valid. */
export const findDeliveryZoneError = (
  params: ValidateZoneParams
): string | undefined => {
  const [problem] = findDeliveryZoneProblems(params);
  return problem ? DELIVERY_ZONE_MESSAGES[problem] : undefined;
};

/**
 * The wire shape for `supplierUpdate`.
 *
 * `deliveryZones` is replaced wholesale server-side, so a save must carry every
 * zone the distributor keeps, not just the edited one.
 */
export const toDeliveryZoneInput = (zones: DeliveryZone[]): DeliveryZone[] =>
  zones.map((zone) => ({
    name: zone.name.trim(),
    points: closeRing(
      zone.points.map((point) => ({
        latitude: point.latitude,
        longitude: point.longitude,
      }))
    ),
  }));

/**
 * Shown when the distributor's areas changed while this tab was open.
 *
 * Saving would replace them with what is on screen, so the areas the other
 * person added would go. Reloading is the only way to see both.
 */
export const CONFLICTING_ZONES_ERROR =
  'Someone else changed this distributor\u2019s areas while this tab was open. Reload the page to see them, then make your changes again.';

/**
 * Whether two sets of areas describe the same thing on the wire.
 *
 * Used to tell a save that failed from one that was written and then failed to
 * announce itself. `supplierService` writes the row, then awaits an SNS
 * publication; a publication failure returns an error with no payload, which
 * looks exactly like a rejected write. Retrying that is not harmless, since the
 * array is replaced wholesale and a colleague's area added in between would be
 * erased.
 *
 * Both sides are put through `toDeliveryZoneInput` first, so a comparison is
 * between what was sent and what came back, not between two spellings of it.
 */
export const areDeliveryZonesEqual = (
  left: DeliveryZone[],
  right: DeliveryZone[]
): boolean =>
  JSON.stringify(toDeliveryZoneInput(left)) ===
  JSON.stringify(toDeliveryZoneInput(right));
